import { useEffect } from 'react';
import { useLocation, useMatch } from 'react-router-dom';
import { SubscriptionService, useApiGetter, useRoutes } from '@localstack/services';
import { Permission, SubscriptionStatus } from '@localstack/types';

import { AppRoute } from '~/config';
import { useAuthProvider } from '~/hooks/useAuthProvider';

/** The routes that we allow the user to be in and not be redirected */
const CHECKOUT_FLOW_ROUTE = [
  AppRoute.PRICING_TRIAL_EXPIRED,
  AppRoute.PRICING_SUBSCRIBE,
  AppRoute.MARKETPLACE_ACTIVATE,
  AppRoute.AZURE_MARKETPLACE_ACTIVATE,
];

/**
 * Hook that redirects user to make a choice on subs page if:
 * - trial is expired
 * - user has never had a subscription
 * - user has no usable subscriptions (i.e. if they had churned)
 */
export const useChooseSubscriptionRedirect = (): void => {
  const { isPlatformAdmin, can } = useAuthProvider();
  const { goto } = useRoutes();
  const { pathname: activeRoutePath } = useLocation();

  const { data: subscriptions } = useApiGetter(SubscriptionService, 'listSubscriptions', [], {
    suppressErrors: true,
    enable: can(Permission.READ_SUBSCRIPTIONS),
  });

  // Do not use .some directly because we might end in different number of hooks between renders situation
  const areWeInCheckoutFlow = CHECKOUT_FLOW_ROUTE.map((route) => useMatch(route)).some(Boolean);
  const isOnboardingPage = activeRoutePath === AppRoute.ONBOARDING;

  useEffect(() => {
    // if user can't read subscriptions we don't know if they need to be redirected
    if (areWeInCheckoutFlow || isOnboardingPage || !can(Permission.READ_SUBSCRIPTIONS)) {
      return;
    }

    const shouldRedirect =
      subscriptions != null && subscriptions?.every((sub) => sub.status === SubscriptionStatus.CANCELED);

    if (shouldRedirect && !isPlatformAdmin) {
      goto(AppRoute.PRICING_TRIAL_EXPIRED, {}, undefined, { refresh: true });
    }
  }, [areWeInCheckoutFlow, subscriptions]);
};
